import {Feature} from '@hconnect/common/enums'
import {Navigate, RouteObject} from 'react-router'

import {PageAssetsSettings} from './modules/assets/components'
import {DefaultAssetIdRedirect} from './modules/assets/components/DefaultAssetIdRedirect'
import {DefaultAssetTypeRedirect} from './modules/assets/components/DefaultAssetTypeRedirect'
import {NewBaseLoadGuard} from './modules/assets/components/NewBaseLoadGuard'
import {PageAsset} from './modules/assets/components/PageAsset'
import {PageAssetListPerType} from './modules/assets/components/PageAssetListPerType'
import {PageNewAsset} from './modules/assets/components/PageNewAsset'
import {PageMaterialSettings} from './modules/materials/components'
import {DefaultMaterialIdRedirect} from './modules/materials/components/DefaultMaterialIdRedirect'
import {DefaultMaterialTypeRedirect} from './modules/materials/components/DefaultMaterialTypeRedirect'
import {
  PageNewMaterialDetails,
  PageExistingMaterialDetails
} from './modules/materials/components/materials-details'
import {MaterialsList} from './modules/materials/components/materials-list/MaterialsList'
import {MaterialTypeGuard} from './modules/materials/components/MaterialTypeGuard'
import {PageMaterialStructure} from './modules/materials/components/PageMaterialStructure'
import {PageMeasurementTypesSettings} from './modules/measurements'
import {
  DefaultPlantNodeRedirect,
  ItemDetails,
  PagePlantStructure,
  PlantStructure,
  DefaultNodeTabRedirect
} from './modules/plant-setup'
import {
  MATERIALS_PERMISSION_TYPES,
  PLANT_ROUTES,
  PLANT_SETUP_PERMISSION_TYPES,
  ROUTES,
  ROUTES_PERMISSIONS,
  getRelPath,
  getUrl
} from './modules/routing'
import {AnyPermissionGuardedRoute, PermissionGuardedRoute} from './modules/routing/components'
import {FeatureGuardedRoute} from './modules/routing/components/FeatureGuardedRoute'
import {PageSamplingPointsSettings} from './modules/sampling-points'
import {PageStorageSettings} from './modules/storages/components'

import {KilnConfigPage} from '@settings/modules/plant-setup/KilnConfigPage'

export const GuardedRoutes: RouteObject[] = [
  {
    path: '',
    element: <PermissionGuardedRoute requiredPermissions={['CREATE_MASTER_DATA']} />,
    children: [
      {
        path: PLANT_ROUTES.KILN_CONFIG.relPath,
        element: <KilnConfigPage />
      }
    ]
  },
  {
    path: '',
    element: <AnyPermissionGuardedRoute requiredPermissions={PLANT_SETUP_PERMISSION_TYPES} />,
    children: [
      {
        path: getRelPath(PLANT_ROUTES.PLANT_SETUP.path),
        element: <PagePlantStructure />,
        children: [
          {
            index: true,
            element: <DefaultPlantNodeRedirect />
          },
          {
            path: getRelPath(PLANT_ROUTES.PLANT_SETUP.NODE.path),
            element: <PlantStructure />,
            children: [
              {
                index: true,
                element: <DefaultNodeTabRedirect />
              },
              {
                path: getRelPath(PLANT_ROUTES.PLANT_SETUP.NODE.NODE_TAB.path),
                element: <ItemDetails />
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: '',
    element: <FeatureGuardedRoute feature={Feature.UpmAssetsAndStorages} shouldInvert={true} />,
    children: [
      {
        path: '',
        element: <PermissionGuardedRoute requiredPermissions={['VIEW_MATERIAL_STORAGE']} />,
        children: [
          {
            path: getRelPath(PLANT_ROUTES.STORAGES.path),
            element: <PageStorageSettings />
          }
        ]
      },
      {
        path: '',
        element: <PermissionGuardedRoute requiredPermissions={['VIEW_ASSETS']} />,
        children: [
          {
            path: getRelPath(PLANT_ROUTES.ASSETS.path),
            element: <PageAssetsSettings />,
            children: [
              {
                index: true,
                element: <DefaultAssetTypeRedirect />
              },
              {
                path: getRelPath(PLANT_ROUTES.ASSETS.ASSET_TYPE.path),
                element: <PageAssetListPerType />,
                children: [
                  {
                    index: true,
                    element: <DefaultAssetIdRedirect />
                  },
                  {
                    path: getRelPath(PLANT_ROUTES.ASSETS.ASSET_TYPE.ID.path),
                    children: [
                      {
                        index: true,
                        element: <DefaultAssetIdRedirect />
                      },
                      {
                        path: getRelPath(PLANT_ROUTES.ASSETS.ASSET_TYPE.ID.ASSET_ID.path),
                        element: <PageAsset />
                      }
                    ]
                  },
                  {
                    path: '',
                    element: <PermissionGuardedRoute requiredPermissions={['CHANGE_ASSETS']} />,
                    children: [
                      {
                        path: '',
                        element: <NewBaseLoadGuard />,
                        children: [
                          {
                            path: getRelPath(PLANT_ROUTES.ASSETS.ASSET_TYPE.NEW_ASSET.path),
                            element: <PageNewAsset />
                          }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: '',
    element: <AnyPermissionGuardedRoute requiredPermissions={MATERIALS_PERMISSION_TYPES} />,
    children: [
      {
        path: getRelPath(PLANT_ROUTES.MATERIALS.path),
        element: <PageMaterialSettings />,
        children: [
          {
            index: true,
            element: <DefaultMaterialTypeRedirect />
          },
          {
            path: getRelPath(PLANT_ROUTES.MATERIALS.TYPE.path),
            children: [
              {
                index: true,
                element: <DefaultMaterialTypeRedirect />
              },
              {
                path: getRelPath(PLANT_ROUTES.MATERIALS.TYPE.MATERIAL_TYPE.path),
                element: <MaterialTypeGuard />,
                children: [
                  {
                    path: 'new',
                    element: <PermissionGuardedRoute requiredPermissions={['CHANGE_MATERIALS']} />,
                    children: [
                      {
                        path: '',
                        element: <PageMaterialStructure isNewMaterial />,
                        children: [
                          {
                            index: true,
                            element: <PageNewMaterialDetails />
                          }
                        ]
                      }
                    ]
                  },
                  {
                    path: '',
                    element: <PageMaterialStructure />,
                    children: [
                      {
                        index: true,
                        element: <MaterialsList />
                      },
                      {
                        path: getRelPath(PLANT_ROUTES.MATERIALS.TYPE.MATERIAL_TYPE.ID.path),
                        children: [
                          {
                            index: true,
                            element: <DefaultMaterialIdRedirect />
                          },
                          {
                            path: getRelPath(
                              PLANT_ROUTES.MATERIALS.TYPE.MATERIAL_TYPE.ID.MATERIAL_ID.path
                            ),
                            element: <PageExistingMaterialDetails />
                          }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: '',
    element: <PermissionGuardedRoute requiredPermissions={[ROUTES_PERMISSIONS.SAMPLING_POINTS]} />,
    children: [
      {
        path: getRelPath(PLANT_ROUTES.SAMPLING_POINTS.path),
        element: <PageSamplingPointsSettings />
      }
    ]
  },
  {
    path: '',
    element: <PermissionGuardedRoute requiredPermissions={[ROUTES_PERMISSIONS.MEASUREMENTS]} />,
    children: [
      {
        path: getRelPath(PLANT_ROUTES.MEASUREMENTS.path),
        element: <PageMeasurementTypesSettings />
      }
    ]
  },
  {
    path: '*',
    element: <Navigate to={getUrl(ROUTES.BASE.NOT_FOUND.path, {})} replace />
  }
]
